import { useRef, useState } from "react";
import { Loader2, Plus, Trash } from "lucide-react";
import { Switch } from "@/components/shadcn/switch";
import { cn } from "@/lib/utils";
import { RichTextValue } from "@/components/Form/RichTextField";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { pickIcon } from "@/components/NewAndIncredibleChatWidget/PresentedFileWidget";
import { StorageService } from "@/services/Storage/storage";
import { FileTypes, isValidFileType } from "@/utils/mimetypes";
import { useSpotUser } from "@/utils/spotUser";
import { AddNewEntry } from "./AssistantDetailsContent";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { NoxusMenu } from "../UI/NoxusMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";

const CONTEXT_FIELD_CONFIG: NoxusFormEntryConfig = {
  type: "str",
  display: {
    type: "rich_text_variables_ai",
    label: "File context",
    connector_name: "",
    enable_heuristics: true,
    heuristic_context:
      "Context for a pre-loaded file: what the file is and when the agent should read it.",
    heuristic_types: ["clarity", "specificity", "actionability"],
  },
};

export type AgentFileEntry = {
  file: {
    id?: string | null;
    uri: string;
    name: string;
    content_type?: string;
  };
  context?: RichTextValue | null;
  enabled?: boolean;
  delimiter?: string | null;
};

const ARCHIVE_TYPES = [FileTypes.ZIP, FileTypes.TAR, FileTypes.GZIP];

// Delimiter options for pre-loaded CSV/TSV files. "auto" is a UI sentinel for
// auto-detect, stored as null (Radix SelectItem can't have an empty value).
const DELIMITER_AUTO = "auto";
const DELIMITER_OPTIONS = [
  { label: "Auto-detect", value: DELIMITER_AUTO },
  { label: "Comma  ,", value: "," },
  { label: "Tab  \\t", value: "\\t" },
  { label: "Semicolon  ;", value: ";" },
  { label: "Pipe  |", value: "|" },
];

const isCsvFile = (f: AgentFileEntry["file"]): boolean => {
  const ct = (f.content_type || "").toLowerCase();
  if (ct === "text/csv" || ct === "text/tab-separated-values") return true;
  const name = f.name.toLowerCase();
  return name.endsWith(".csv") || name.endsWith(".tsv");
};

const FileRow = ({
  entry,
  isDisabled,
  onChange,
  onRemove,
}: {
  entry: AgentFileEntry;
  isDisabled?: boolean;
  onChange: (next: AgentFileEntry) => void;
  onRemove: () => void;
}) => {
  const { Icon, iconClass, bgClass } = pickIcon(entry.file.content_type || "");
  const enabled = entry.enabled ?? true;
  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-md",
              bgClass,
              iconClass
            )}
          >
            <Icon className="size-4" />
          </div>
          <p className="text-sm font-medium truncate flex-1 min-w-0">
            {entry.file.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            disabled={isDisabled}
            onCheckedChange={(checked) =>
              onChange({ ...entry, enabled: checked })
            }
          />
          <NoxusMenu
            isSmall
            isDisabled={isDisabled}
            itemsArray={[
              {
                label: "Remove",
                icon: Trash,
                onClick: onRemove,
                color: "destructive",
              },
            ]}
          />
        </div>
      </div>
      {enabled && (
        <ExpandableTextarea
          value={entry.context ?? ""}
          onChange={(value) => onChange({ ...entry, context: value })}
          isDisabled={isDisabled}
          label="File context"
          placeholder="What is this file? When should the agent read it?"
          fieldConfig={CONTEXT_FIELD_CONFIG}
        />
      )}
      {enabled && isCsvFile(entry.file) && (
        <div className="flex items-center justify-between gap-2 px-3 pb-3">
          <p className="text-xs text-muted-foreground">Delimiter</p>
          <Select
            value={entry.delimiter ?? DELIMITER_AUTO}
            disabled={isDisabled}
            onValueChange={(value) =>
              onChange({
                ...entry,
                delimiter: value === DELIMITER_AUTO ? null : value,
              })
            }
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue placeholder="Auto-detect" />
            </SelectTrigger>
            <SelectContent>
              {DELIMITER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export const FilesField = ({
  files,
  onFilesChange,
  isDisabled,
}: {
  files: AgentFileEntry[] | undefined;
  onFilesChange: (files: AgentFileEntry[]) => void;
  isDisabled?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const list = files ?? [];

  const update = (next: AgentFileEntry[]) => {
    if (!isDisabled) onFilesChange(next);
  };

  const onUpload = async (picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    const files = Array.from(picked).filter(
      (f) => !isValidFileType(f, ARCHIVE_TYPES)
    );
    if (files.length === 0) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const results = await Promise.all(
        files.map((f) => {
          const data = new FormData();
          data.append("file", f);
          return StorageService.uploadFile(spotGroup.id, data);
        })
      );
      const added: AgentFileEntry[] = results.map((sf) => ({
        file: {
          id: sf.id,
          uri: `spot://${sf.id}`,
          name: sf.filename,
          content_type: sf.content_type,
        },
        context: null,
        enabled: true,
      }));
      update([...list, ...added]);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-sm font-medium text-foreground leading-none">
          Files
        </p>
        <p className="text-xs font-regular text-muted-foreground mt-1.5">
          Files pre-loaded into every chat, with context.
        </p>
      </div>
      {list.map((entry, idx) => (
        <FileRow
          key={entry.file.uri || entry.file.id || idx}
          entry={entry}
          isDisabled={isDisabled}
          onChange={(next) =>
            update(list.map((e, i) => (i === idx ? next : e)))
          }
          onRemove={() => update(list.filter((_, i) => i !== idx))}
        />
      ))}

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        disabled={isDisabled}
        onChange={(e) => onUpload(e.target.files)}
      />
      {uploading ? (
        <div className="flex gap-2 items-center justify-between px-2 py-1.5 border rounded-md w-full opacity-50 cursor-not-allowed">
          <div className="flex gap-2 items-center">
            <div className="flex bg-accent text-muted-foreground p-1.5 rounded-md justify-center items-center">
              <Plus width={16} height={16} />
            </div>
            <p className="text-sm">Uploading…</p>
          </div>
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <AddNewEntry
          label="Upload files"
          onClick={() => inputRef.current?.click()}
          isDisabled={isDisabled}
        />
      )}
    </div>
  );
};
